/**
 * Data layer for `digitalocean-spaces-export-csv-json`. Thin typed fetch wrappers over /api/v1/digitalocean-spaces-export-csv-json.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DigitaloceanSpacesExportCsvJsonRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDigitaloceanSpacesExportCsvJsonResponse {
	count: number;
	items: DigitaloceanSpacesExportCsvJsonRow[];
}

export interface CreateDigitaloceanSpacesExportCsvJsonBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/digitalocean-spaces-export-csv-json";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDigitaloceanSpacesExportCsvJsonResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDigitaloceanSpacesExportCsvJsonResponse>(res);
}

export async function create(body: CreateDigitaloceanSpacesExportCsvJsonBody): Promise<DigitaloceanSpacesExportCsvJsonRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DigitaloceanSpacesExportCsvJsonRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const digitaloceanSpacesExportCsvJsonQueryKeys = {
	all: ["digitalocean-spaces-export-csv-json"] as const,
	list: () => [...digitaloceanSpacesExportCsvJsonQueryKeys.all] as const,
	detail: (id: string) => [...digitaloceanSpacesExportCsvJsonQueryKeys.all, id] as const,
};
